// Service d'analyse du diagnostic "Soignez votre projet"

// Définition des axes et leurs questions (FR + EN)
export const DIAGNOSTIC_AXES = [
  {
    id: 'vision',
    name: 'Vision & objectifs',
    nameEn: 'Vision & objectives',
    questions: [
      'Les objectifs du projet sont clairement définis et compris par tous.',
      'Le succès du projet est mesurable par des critères précis.',
      'Les livrables prioritaires sont connus sans ambiguïté.',
      'L\'équipe sait exactement pourquoi ce projet est important.'
    ],
    questionsEn: [
      'Project objectives are clearly defined and understood by all.',
      'Project success is measurable by precise criteria.',
      'Priority deliverables are known without ambiguity.',
      'The team knows exactly why this project is important.'
    ]
  },
  {
    id: 'priorities',
    name: 'Priorités & planification',
    nameEn: 'Priorities & planning',
    questions: [
      'Les priorités sont stables et rarement contradictoires.',
      'Le planning est réaliste et régulièrement mis à jour.',
      'Les tâches critiques sont clairement identifiées.',
      'Nous savons sur quoi ne pas travailler.'
    ],
    questionsEn: [
      'Priorities are stable and rarely contradictory.',
      'Planning is realistic and regularly updated.',
      'Critical tasks are clearly identified.',
      'We know what not to work on.'
    ]
  },
  {
    id: 'governance',
    name: 'Gouvernance & décisions',
    nameEn: 'Governance & decisions',
    questions: [
      'Les rôles et responsabilités sont clairement définis.',
      'Les décisions sont prises au bon niveau.',
      'Les arbitrages sont rapides et assumés.',
      'Les blocages sont traités sans attendre.'
    ],
    questionsEn: [
      'Roles and responsibilities are clearly defined.',
      'Decisions are made at the right level.',
      'Arbitrations are quick and assumed.',
      'Blockages are addressed without delay.'
    ]
  },
  {
    id: 'communication',
    name: 'Réunions & communication',
    nameEn: 'Meetings & communication',
    questions: [
      'Les réunions ont un objectif clair et une durée maîtrisée.',
      'Les décisions prises en réunion sont formalisées et suivies.',
      'L\'information circule de manière fluide.',
      'Les malentendus sont rares.'
    ],
    questionsEn: [
      'Meetings have a clear objective and controlled duration.',
      'Decisions made in meetings are formalized and followed up.',
      'Information flows smoothly.',
      'Misunderstandings are rare.'
    ]
  },
  {
    id: 'execution',
    name: 'Exécution & performance',
    nameEn: 'Execution & performance',
    questions: [
      'L\'avancement réel est visible et partagé.',
      'Les écarts sont détectés tôt.',
      'Les actions correctives sont mises en œuvre rapidement.',
      'L\'équipe a le sentiment d\'avancer efficacement.'
    ],
    questionsEn: [
      'Real progress is visible and shared.',
      'Deviations are detected early.',
      'Corrective actions are implemented quickly.',
      'The team feels they are moving forward effectively.'
    ]
  },
  {
    id: 'climate',
    name: 'Climat & énergie du projet',
    nameEn: 'Climate & project energy',
    questions: [
      'Le niveau de stress est maîtrisé.',
      'Les tensions sont exprimées et traitées.',
      'L\'équipe est engagée et motivée.',
      'Le projet génère plus d\'énergie qu\'il n\'en consomme.'
    ],
    questionsEn: [
      'Stress level is under control.',
      'Tensions are expressed and addressed.',
      'The team is engaged and motivated.',
      'The project generates more energy than it consumes.'
    ]
  }
];

// Calculer le score d'un axe (moyenne des réponses)
export const calculateAxisScore = (responses) => {
  if (!responses || responses.length === 0) return 0;
  const sum = responses.reduce((acc, val) => acc + val, 0);
  return (sum / responses.length).toFixed(2);
};

// Calculer le score global (moyenne de tous les axes)
export const calculateGlobalScore = (axisScores) => {
  if (!axisScores || axisScores.length === 0) return 0;
  const sum = axisScores.reduce((acc, val) => acc + parseFloat(val), 0);
  return (sum / axisScores.length).toFixed(2);
};

// Classifier un score (avec support multilingue)
export const classifyScore = (score, lang = 'fr') => {
  const numScore = parseFloat(score);
  if (numScore >= 4.1) {
    return { 
      level: 'mastered', 
      label: lang === 'fr' ? 'Zone maîtrisée' : 'Mastered zone', 
      color: 'green', 
      icon: '🟢' 
    };
  }
  if (numScore >= 3.0) {
    return { 
      level: 'fragile', 
      label: lang === 'fr' ? 'Zone fragile' : 'Fragile zone', 
      color: 'orange', 
      icon: '🟠' 
    };
  }
  return { 
    level: 'critical', 
    label: lang === 'fr' ? 'Zone critique' : 'Critical zone', 
    color: 'red', 
    icon: '🔴' 
  };
};

// Obtenir les problèmes associés à un axe selon son score (avec support multilingue)
export const getAxisProblems = (axisId, score, lang = 'fr') => {
  const problemsFr = {
    vision: {
      critical: {
        problem: 'Travail intense sans résultats visibles',
        manifestation: 'Activité élevée, peu de livrables finalisés',
        risk: 'Perte de valeur du projet'
      },
      fragile: {
        problem: 'Alignement partiel',
        manifestation: 'Discussions fréquentes sur le "bon objectif"',
        risk: 'Dilution des efforts'
      }
    },
    priorities: {
      critical: {
        problem: 'Confusion sur les priorités',
        manifestation: 'Changements permanents, multitâche excessif',
        risk: 'Retards structurels'
      },
      fragile: {
        problem: 'Priorités instables',
        manifestation: 'Replanifications répétées',
        risk: 'Surconsommation de ressources'
      }
    },
    governance: {
      critical: {
        problem: 'Décisions retardées',
        manifestation: 'Attente de validations, escalades tardives',
        risk: 'Paralysie décisionnelle'
      },
      fragile: {
        problem: 'Arbitrages lents',
        manifestation: 'Décisions prises trop tard',
        risk: 'Opportunités manquées'
      }
    },
    communication: {
      critical: {
        problem: 'Réunions inefficaces',
        manifestation: 'Répétitions, absence de décisions',
        risk: 'Perte de temps collectif'
      },
      fragile: {
        problem: 'Communication incomplète',
        manifestation: 'Malentendus, re-travail',
        risk: 'Baisse de qualité'
      }
    },
    execution: {
      critical: {
        problem: 'Illusion d\'avancement',
        manifestation: 'Découverte tardive des écarts',
        risk: 'Effet tunnel, crise tardive'
      },
      fragile: {
        problem: 'Pilotage réactif',
        manifestation: 'Corrections tardives',
        risk: 'Dérapages coûts/délais'
      }
    },
    climate: {
      critical: {
        problem: 'Stress diffus et flou organisationnel',
        manifestation: 'Fatigue, tensions silencieuses',
        risk: 'Désengagement collectif'
      },
      fragile: {
        problem: 'Énergie instable',
        manifestation: 'Motivation fluctuante',
        risk: 'Usure progressive de l\'équipe'
      }
    }
  };

  const problemsEn = {
    vision: {
      critical: {
        problem: 'Intense work without visible results',
        manifestation: 'High activity, few finalized deliverables',
        risk: 'Loss of project value'
      },
      fragile: {
        problem: 'Partial alignment',
        manifestation: 'Frequent discussions about the "right objective"',
        risk: 'Dilution of efforts'
      }
    },
    priorities: {
      critical: {
        problem: 'Confusion about priorities',
        manifestation: 'Permanent changes, excessive multitasking',
        risk: 'Structural delays'
      },
      fragile: {
        problem: 'Unstable priorities',
        manifestation: 'Repeated replanning',
        risk: 'Resource overconsumption'
      }
    },
    governance: {
      critical: {
        problem: 'Delayed decisions',
        manifestation: 'Waiting for validations, late escalations',
        risk: 'Decision paralysis'
      },
      fragile: {
        problem: 'Slow arbitrations',
        manifestation: 'Decisions made too late',
        risk: 'Missed opportunities'
      }
    },
    communication: {
      critical: {
        problem: 'Ineffective meetings',
        manifestation: 'Repetitions, absence of decisions',
        risk: 'Collective time waste'
      },
      fragile: {
        problem: 'Incomplete communication',
        manifestation: 'Misunderstandings, rework',
        risk: 'Quality decrease'
      }
    },
    execution: {
      critical: {
        problem: 'Illusion of progress',
        manifestation: 'Late discovery of deviations',
        risk: 'Tunnel effect, late crisis'
      },
      fragile: {
        problem: 'Reactive steering',
        manifestation: 'Late corrections',
        risk: 'Cost/schedule overruns'
      }
    },
    climate: {
      critical: {
        problem: 'Diffuse stress and organizational blur',
        manifestation: 'Fatigue, silent tensions',
        risk: 'Collective disengagement'
      },
      fragile: {
        problem: 'Unstable energy',
        manifestation: 'Fluctuating motivation',
        risk: 'Progressive team burnout'
      }
    }
  };

  const problems = lang === 'fr' ? problemsFr : problemsEn;
  const classification = classifyScore(score, lang);
  const axisProblems = problems[axisId];
  
  if (!axisProblems) return null;
  
  if (classification.level === 'critical') return axisProblems.critical;
  if (classification.level === 'fragile') return axisProblems.fragile;
  return null;
};

// Actions recommandées par axe (avec support multilingue)
const getRecommendedAction = (axisId, level, lang = 'fr') => {
  const actionsFr = {
    vision: {
      critical: 'Formaliser 3 objectifs SMART validés par le top management',
      fragile: 'Définir 3 KPI de succès partagés avec l\'équipe'
    },
    priorities: {
      critical: 'Mettre en place un rituel d\'arbitrage hebdomadaire',
      fragile: 'Identifier et piloter le chemin critique du projet'
    },
    governance: {
      critical: 'Clarifier le RACI sur les décisions clés',
      fragile: 'Fixer des délais maximum de décision'
    },
    communication: {
      critical: 'Imposer un agenda obligatoire avec objectif décisionnel',
      fragile: 'Mettre en place un canal unique de communication projet'
    },
    execution: {
      critical: 'Créer un tableau d\'avancement visuel partagé',
      fragile: 'Organiser des revues d\'avancement courtes et fréquentes'
    },
    climate: {
      critical: 'Clarifier les attentes et marges de manœuvre',
      fragile: 'Créer des espaces sécurisés de parole'
    }
  };

  const actionsEn = {
    vision: {
      critical: 'Formalize 3 SMART objectives validated by top management',
      fragile: 'Define 3 success KPIs shared with the team'
    },
    priorities: {
      critical: 'Implement a weekly arbitration ritual',
      fragile: 'Identify and manage the project critical path'
    },
    governance: {
      critical: 'Clarify the RACI on key decisions',
      fragile: 'Set maximum decision deadlines'
    },
    communication: {
      critical: 'Impose a mandatory agenda with decision objective',
      fragile: 'Establish a single project communication channel'
    },
    execution: {
      critical: 'Create a shared visual progress board',
      fragile: 'Organize short and frequent progress reviews'
    },
    climate: {
      critical: 'Clarify expectations and room for maneuver',
      fragile: 'Create safe spaces for dialogue'
    }
  };
  
  const actions = lang === 'fr' ? actionsFr : actionsEn;
  const defaultAction = lang === 'fr' 
    ? 'Analyser en détail les causes et mettre en place un plan d\'action'
    : 'Analyze causes in detail and implement an action plan';
  
  return actions[axisId]?.[level] || defaultAction;
};

// Générer des recommandations prioritaires (avec support multilingue)
export const generateRecommendations = (analysisResults, lang = 'fr') => {
  const recommendations = [];
  
  analysisResults.forEach(axis => {
    const classification = classifyScore(axis.score, lang);
    
    if (classification.level === 'critical' || classification.level === 'fragile') {
      const problems = getAxisProblems(axis.id, axis.score, lang);
      if (problems) {
        recommendations.push({
          axis: axis.name,
          priority: classification.level === 'critical' 
            ? (lang === 'fr' ? 'Urgent' : 'Urgent')
            : (lang === 'fr' ? 'Important' : 'Important'),
          problem: problems.problem,
          risk: problems.risk,
          action: getRecommendedAction(axis.id, classification.level, lang)
        });
      }
    }
  });
  
  // Trier par priorité (critique d'abord)
  return recommendations.sort((a, b) => {
    const urgentLabel = lang === 'fr' ? 'Urgent' : 'Urgent';
    if (a.priority === urgentLabel && b.priority !== urgentLabel) return -1;
    if (a.priority !== urgentLabel && b.priority === urgentLabel) return 1;
    return 0;
  });
};

// Générer le résumé exécutif (avec support multilingue)
export const generateExecutiveSummary = (globalScore, analysisResults, lang = 'fr') => {
  const classification = classifyScore(globalScore, lang);
  const criticalAxes = analysisResults.filter(a => classifyScore(a.score, lang).level === 'critical');
  const fragileAxes = analysisResults.filter(a => classifyScore(a.score, lang).level === 'fragile');
  
  return {
    globalScore,
    classification,
    criticalCount: criticalAxes.length,
    fragileCount: fragileAxes.length,
    masteredCount: analysisResults.length - criticalAxes.length - fragileAxes.length,
    topRisks: criticalAxes.slice(0, 3).map(axis => ({
      axis: axis.name,
      score: axis.score,
      problems: getAxisProblems(axis.id, axis.score, lang)
    }))
  };
};
